import { tabAccount } from '@dugzi/ui';
import { APP, ApiError, api } from './api';
import type { DriveNode } from './types';

/**
 * Uploads: files go up in 8 MB chunks, three files at a time, each chunk retried with backoff. An
 * upload remembers its server ID (by name, size, date and folder), so choosing the same file again
 * after a dropped connection or a reload resumes where it stopped instead of starting over.
 */

export type UploadState = 'queued' | 'uploading' | 'done' | 'failed' | 'cancelled';

export interface UploadItem {
  key: number;
  name: string;
  size: number;
  sent: number;
  state: UploadState;
  error?: string;
  node?: DriveNode;
  /** Folder it goes into (fld_… or root) and, for folder uploads, the folders in between. */
  parentId: string;
  path?: string;
}

interface Internal extends UploadItem { file: File; xhr?: XMLHttpRequest; cancelled?: boolean }

type Listener = (items: UploadItem[]) => void;
type DoneListener = (node: DriveNode, item: UploadItem) => void;

const PARALLEL = 3;
const RETRIES = 5;
let seq = 0;
const items: Internal[] = [];
const listeners = new Set<Listener>();
const doneListeners = new Set<DoneListener>();

function emit() {
  const snap = items.map(({ file: _f, xhr: _x, cancelled: _c, ...rest }) => ({ ...rest }));
  listeners.forEach((l) => l(snap));
}

export function subscribe(l: Listener): () => void {
  listeners.add(l);
  l(items.map(({ file: _f, xhr: _x, cancelled: _c, ...rest }) => ({ ...rest })));
  return () => { listeners.delete(l); };
}

export function onUploaded(l: DoneListener): () => void {
  doneListeners.add(l);
  return () => { doneListeners.delete(l); };
}

function resumeKey(f: File, parentId: string, path?: string) {
  return `dz.drive.upload.${tabAccount.get(APP) ?? ''}.${parentId}.${path ?? ''}.${f.name}.${f.size}.${f.lastModified}`;
}

export function enqueue(files: { file: File; path?: string }[], parentId: string) {
  for (const { file, path } of files) {
    items.push({ key: ++seq, name: file.name, size: file.size, sent: 0, state: 'queued', parentId, path, file });
  }
  emit();
  pump();
}

export function cancel(key: number) {
  const it = items.find((i) => i.key === key);
  if (!it || it.state === 'done') return;
  it.cancelled = true;
  it.xhr?.abort();
  if (it.state !== 'uploading') { it.state = 'cancelled'; emit(); }
}

export function retry(key: number) {
  const it = items.find((i) => i.key === key);
  if (!it || (it.state !== 'failed' && it.state !== 'cancelled')) return;
  it.state = 'queued'; it.cancelled = false; it.error = undefined;
  emit();
  pump();
}

export function clearFinished() {
  for (let i = items.length - 1; i >= 0; i--) if (['done', 'cancelled'].includes(items[i].state)) items.splice(i, 1);
  emit();
}

function pump() {
  const running = items.filter((i) => i.state === 'uploading').length;
  const next = items.filter((i) => i.state === 'queued').slice(0, Math.max(0, PARALLEL - running));
  for (const it of next) run(it);
}

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

function putChunk(it: Internal, uploadId: string, idx: number, blob: Blob, base: number): Promise<void> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    it.xhr = xhr;
    xhr.open('PUT', `/api/uploads/${uploadId}/chunks/${idx}`);
    const acc = tabAccount.get(APP);
    if (acc) xhr.setRequestHeader('X-Dugzi-Account', acc);
    xhr.setRequestHeader('Content-Type', 'application/octet-stream');
    xhr.upload.onprogress = (e) => { it.sent = base + e.loaded; emit(); };
    xhr.onload = () => {
      if (xhr.status === 204) return resolve();
      let msg = 'This part of the file could not be sent.';
      try { msg = JSON.parse(xhr.responseText)?.error?.message ?? msg; } catch { /* keep */ }
      reject(new ApiError(msg, 'chunk', xhr.status));
    };
    xhr.onerror = () => reject(new ApiError("You're offline, or Dugzi Drive can't be reached right now.", 'network', 0));
    xhr.onabort = () => reject(new ApiError('Cancelled', 'cancelled', 0));
    xhr.send(blob);
  });
}

async function run(it: Internal) {
  it.state = 'uploading'; it.sent = 0; emit();
  const key = resumeKey(it.file, it.parentId, it.path);
  try {
    let up: { id: string; chunkSize: number; chunks: number; received: number[] } | null = null;
    const saved = (() => { try { return localStorage.getItem(key); } catch { return null; } })();
    if (saved) up = await api(`/api/uploads/${saved}`).catch(() => null);
    if (!up) {
      up = await api('/api/uploads', { body: { parentId: it.parentId, name: it.name, size: it.size, ...(it.path ? { path: it.path } : {}) } });
      try { localStorage.setItem(key, up!.id); } catch { /* private mode: no resume after reload */ }
    }
    const u = up!;
    const have = new Set(u.received);
    let done = 0;
    for (let i = 0; i < u.chunks; i++) {
      const len = Math.min(u.chunkSize, it.size - i * u.chunkSize);
      if (have.has(i)) { done += Math.max(0, len); it.sent = done; emit(); continue; }
      if (it.cancelled) throw new ApiError('Cancelled', 'cancelled', 0);
      const blob = it.file.slice(i * u.chunkSize, i * u.chunkSize + Math.max(0, len));
      for (let attempt = 0; ; attempt++) {
        try { await putChunk(it, u.id, i, blob, done); break; } catch (e) {
          const err = e as ApiError;
          if (err.code === 'cancelled' || it.cancelled) throw err;
          if (attempt >= RETRIES || (err.status >= 400 && err.status < 500 && err.status !== 408 && err.status !== 429)) throw err;
          await sleep(1000 * 2 ** attempt);
        }
      }
      done += Math.max(0, len);
      it.sent = done; emit();
    }
    const node = await api<DriveNode>(`/api/uploads/${u.id}/complete`, { body: {} });
    try { localStorage.removeItem(key); } catch { /* fine */ }
    it.state = 'done'; it.sent = it.size; it.node = node; it.xhr = undefined;
    emit();
    doneListeners.forEach((l) => l(node, it));
  } catch (e) {
    const err = e as ApiError;
    if (it.cancelled || err.code === 'cancelled') {
      it.state = 'cancelled';
      const saved = (() => { try { return localStorage.getItem(key); } catch { return null; } })();
      if (saved) { api(`/api/uploads/${saved}`, { method: 'DELETE' }).catch(() => {}); try { localStorage.removeItem(key); } catch { /* fine */ } }
    } else {
      it.state = 'failed'; it.error = err.message;
    }
    it.xhr = undefined;
    emit();
  }
  pump();
}

/** Files (and folders, with their paths) from a drop. */
export async function filesFromDrop(dt: DataTransfer): Promise<{ file: File; path?: string }[]> {
  const entries = Array.from(dt.items ?? []).map((i) => (i.kind === 'file' ? (i as DataTransferItem & { webkitGetAsEntry?: () => FileSystemEntry | null }).webkitGetAsEntry?.() : null));
  if (!entries.some((e) => e && e.isDirectory)) return Array.from(dt.files).map((file) => ({ file }));
  const out: { file: File; path?: string }[] = [];
  const walk = async (entry: FileSystemEntry, dir: string): Promise<void> => {
    if (entry.isFile) {
      const file = await new Promise<File>((res, rej) => (entry as FileSystemFileEntry).file(res, rej));
      out.push({ file, path: dir || undefined });
    } else if (entry.isDirectory) {
      const reader = (entry as FileSystemDirectoryEntry).createReader();
      const sub = dir ? `${dir}/${entry.name}` : entry.name;
      for (;;) {
        const batch = await new Promise<FileSystemEntry[]>((res, rej) => reader.readEntries(res, rej));
        if (!batch.length) break;
        for (const b of batch) await walk(b, sub);
      }
    }
  };
  for (const e of entries) if (e) await walk(e, '');
  return out;
}

/** Files from a folder picker (`webkitdirectory`): each keeps its folders. */
export function filesFromPicker(list: FileList): { file: File; path?: string }[] {
  return Array.from(list).map((file) => {
    const rel = (file as File & { webkitRelativePath?: string }).webkitRelativePath ?? '';
    const dir = rel.includes('/') ? rel.slice(0, rel.lastIndexOf('/')) : '';
    return { file, path: dir || undefined };
  });
}
